import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { FiLogOut, FiPlay, FiLock, FiCheckCircle, FiBook, FiUser, FiMenu, FiDivide, FiHexagon, FiTrendingUp, FiMessageSquare } from 'react-icons/fi';
import Sidebar from '../components/Sidebar';
import fundoImg from '../assets/fundo.png';

const dashboardBackgroundStyle = {
  backgroundImage: `url(${fundoImg})`,
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center'
};

const API_URL = process.env.REACT_APP_API_URL || '/api';

const Dashboard = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'admin_master') {
      navigate('/admin/dashboard');
      return;
    }
    fetchCategories();
  }, [user]);

  useEffect(() => {
    if (selectedCategory) {
      fetchChaptersForCategory(selectedCategory.id);
    }
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/categories`);
      if (response.data && response.data.categories) {
        setCategories(response.data.categories);
      } else {
        console.error('Resposta inválida da API:', response.data);
      }
    } catch (error) {
      console.error('Erro ao buscar categorias:', error);
      console.error('Detalhes do erro:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const fetchChaptersForCategory = async (categoryId) => {
    try {
      const response = await axios.get(`${API_URL}/categories/${categoryId}`);
      setChapters(response.data.chapters);
    } catch (error) {
      console.error('Erro ao buscar capítulos:', error);
    }
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
  };

  const handleChapterClick = (chapter, index) => {
    const isUnlocked = chapter.isUnlocked !== false;
    if (isUnlocked) {
      navigate(`/chapter/${chapter.id}`);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getChapterStatus = (chapter, index) => {
    if (chapter.completed) {
      return { icon: FiCheckCircle, color: 'text-green-500', bgColor: 'bg-green-100', text: 'Concluído' };
    }
    const isUnlocked = chapter.isUnlocked !== false;
    if (isUnlocked) {
      return { icon: FiPlay, color: 'text-rose-500', bgColor: 'bg-rose-100', text: 'Disponível' };
    }
    return { icon: FiLock, color: 'text-gray-400', bgColor: 'bg-gray-100', text: 'Bloqueado' };
  };

  const getCategoryIcon = (categoryName) => {
    const name = categoryName.toLowerCase();
    if (name.includes('álgebra') || name.includes('algebra')) return FiTrendingUp;
    if (name.includes('geometria')) return FiHexagon;
    if (name.includes('aritmética') || name.includes('aritmetica')) return FiDivide;
    return FiBook;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-gray-600 text-xl font-bold animate-pulse">Carregando módulos...</div>
      </div>
    );
  }

  if (!selectedCategory) {
    return (
      <div className="min-h-screen flex flex-col bg-slate-100" style={dashboardBackgroundStyle}>
        <header className="bg-white/95 backdrop-blur-sm border-b border-gray-200 px-6 py-4 sticky top-0 z-30 shadow-sm">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div>
              <h1 className="text-3xl font-black tracking-tighter drop-shadow-sm">
                 <span className="text-gray-900">J</span>
                  <span className="text-rose-600">&</span>
                   <span className="text-gray-900">M</span>
              </h1>
              <p className="text-sm text-gray-600 font-medium">Bem-vindo, {user?.username}!</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <FiUser className="text-gray-400" />
                <span>{user?.username}</span>
              </div>
              <button
                onClick={() => navigate('/tutoria')}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
              >
                <FiMessageSquare />
                <span className="hidden sm:inline">Tutoria</span>
              </button>
              <button
                onClick={() => navigate('/profile')}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
              >
                <FiUser />
                <span className="hidden sm:inline">Perfil</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
              >
                <FiLogOut />
                <span className="hidden sm:inline">Sair</span>
              </button>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-6 py-12">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-800 mb-2">Escolha um Tema</h2>
            <p className="text-gray-600">Selecione uma categoria para começar a aprender</p>
          </div>

          {categories.length === 0 ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
              <p className="text-yellow-800 font-medium mb-2">Nenhuma categoria encontrada</p>
              <p className="text-yellow-600 text-sm">
                Por favor, recrie o banco de dados. Delete o arquivo <code className="bg-yellow-100 px-2 py-1 rounded">server/database/database.sqlite</code> e reinicie o servidor.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories.map((category) => {
                const CategoryIcon = getCategoryIcon(category.name);
                const isUnlocked = category.isUnlocked !== false;
                
                return (
                  <div
                    key={category.id}
                    onClick={() => {
                      if (isUnlocked) {
                        handleCategorySelect(category);
                      } else {
                        alert('Complete todas as questões do tema anterior para desbloquear este tema!');
                      }
                    }}
                    className={`bg-white rounded-xl shadow-sm border p-6 transition-all group ${
                      isUnlocked 
                        ? 'border-gray-200 cursor-pointer hover:shadow-md hover:border-rose-300' 
                        : 'border-gray-300 opacity-60 cursor-not-allowed'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`p-4 rounded-lg transition ${
                        isUnlocked 
                          ? 'bg-rose-100 group-hover:bg-rose-200' 
                          : 'bg-gray-100'
                      }`}>
                        <CategoryIcon className={`text-2xl ${isUnlocked ? 'text-rose-600' : 'text-gray-400'}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className={`text-xl font-bold mb-0 ${
                            isUnlocked ? 'text-gray-800' : 'text-gray-500'
                          }`}>
                            {category.name}
                          </h3>
                          {!isUnlocked && (
                            <FiLock className="text-gray-400" />
                          )}
                        </div>
                        <p className={`text-sm mb-4 ${isUnlocked ? 'text-gray-600' : 'text-gray-400'}`}>
                          {category.description}
                        </p>
                        <div className={`flex items-center gap-2 text-sm font-medium ${
                          isUnlocked ? 'text-rose-600' : 'text-gray-400'
                        }`}>
                          <span>{category.chapter_count || 0} capítulos</span>
                          {isUnlocked && <FiPlay className="text-xs" />}
                        </div>
                        {!isUnlocked && (
                          <p className="text-xs text-red-500 mt-2 font-medium">
                            Complete o tema anterior para desbloquear
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-100 overflow-hidden" style={dashboardBackgroundStyle}>
      <Sidebar 
        isOpen={sidebarOpen} 
        onClose={() => setSidebarOpen(false)}
        categoryId={selectedCategory.id}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white/95 backdrop-blur-sm border-b border-gray-200 px-6 py-4 shadow-sm z-30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden text-gray-600 hover:text-gray-900"
              >
                <FiMenu className="text-xl" />
              </button>
              <button
                onClick={() => setSelectedCategory(null)}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
              >
                <FiBook />
                <span className="hidden sm:inline">Voltar para Categorias</span>
              </button>
              <div className="hidden md:block">
                <h1 className="text-lg font-semibold text-gray-800">{selectedCategory.name}</h1>
                <p className="text-sm text-gray-600">{selectedCategory.description}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <FiUser className="text-gray-400" />
                <span>{user?.username}</span>
              </div>
              <button
                onClick={() => navigate('/tutoria')}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
              >
                <FiMessageSquare />
                <span className="hidden sm:inline">Tutoria</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
              >
                <FiLogOut />
                <span className="hidden sm:inline">Sair</span>
              </button>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {chapters.map((chapter, index) => {
              const status = getChapterStatus(chapter, index);
              const StatusIcon = status.icon;
              const isUnlocked = chapter.isUnlocked !== false;

              return (
                <div
                  key={chapter.id}
                  onClick={() => handleChapterClick(chapter, index)}
                  className={`bg-white rounded-xl shadow-sm border p-6 transition-all group ${
                    isUnlocked 
                      ? 'border-gray-200 cursor-pointer hover:shadow-md hover:border-rose-300' 
                      : 'border-gray-300 opacity-60 cursor-not-allowed'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-4 rounded-lg ${status.bgColor}`}>
                      <StatusIcon className={`text-2xl ${status.color}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className={`text-xl font-bold mb-2 ${
                        isUnlocked ? 'text-gray-800' : 'text-gray-500'
                      }`}>
                        {chapter.title}
                      </h3>
                      <p className={`text-sm mb-4 ${isUnlocked ? 'text-gray-600' : 'text-gray-400'}`}>
                        {chapter.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-semibold px-2 py-1 rounded ${status.bgColor} ${status.color}`}>
                          {status.text}
                        </span>
                        <span className="text-xs text-gray-400">
                          Capítulo {chapter.order_index}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
